import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PaymentIcon from "@mui/icons-material/Payment";
import PercentIcon from "@mui/icons-material/Percent";
import PrintIcon from "@mui/icons-material/Print";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import StorefrontIcon from "@mui/icons-material/Storefront";
import {
    Box,
    Button,
    CircularProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import useFetchCompany from "../../../data/hooks/useFetchCompany";
import { getAllMyReports } from "../../../services/requests/ApiRequests";
import {
    CONSTANT_PAYMENT_METHODS,
    CONSTANT_PAYMENT_METHODS_DICT,
} from "../../constants";
import { PrintTable } from "./components/PrintTable";

const PeriodicReports = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const registerId = searchParams.get("rid");
    const dateFrom = searchParams.get("date_from");
    const dateTo = searchParams.get("date_to");
    const fromReportId = searchParams.get("from_report_id");
    const toReportId = searchParams.get("to_report_id");
    const [reports, setReports] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uniqueTaxes, setUniqueTaxes] = useState([]);
    const [usedPaymentMethods, setUsedPaymentMethods] = useState([]);
    const [totals, setTotals] = useState({
        receipts: 0,
    });
    const { company, loading: isCompanyLoading } = useFetchCompany({
        skip: 0,
        limit: 20,
    });

    const printRef = useRef(null);

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        pageStyle: `
    @page {
      size: portrait;
      margin: 10mm;
    }
    @media print {
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      table {
        width: 100% !important;
      }
    }
  `,
    });

    const formatCurrency = (value) => {
        if (value === null || value === undefined || isNaN(parseFloat(value))) {
            return "0,00";
        }

        try {
            return new Intl.NumberFormat("ro-MD", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(parseFloat(value));
        } catch (e) {
            console.error("Error formatting currency:", e);
            return "0,00";
        }
    };

    const calculateTotals = (reports, taxes, paymentMethods) => {
        if (!reports || !Array.isArray(reports) || reports.length === 0) {
            const emptyResult = {
                receipts: 0,
            };

            if (taxes && Array.isArray(taxes)) {
                taxes.forEach((tax) => {
                    const key = `${tax.code}-${tax.percent}`;
                    emptyResult[`${key}-total`] = "0.00";
                    emptyResult[`${key}-tva`] = "0.00";
                });
            }

            if (paymentMethods && Array.isArray(paymentMethods)) {
                paymentMethods.forEach((method) => {
                    emptyResult[`payment-${method.type}`] = "0.00";
                });
            }

            return emptyResult;
        }

        const result = {
            receipts: 0,
        };

        if (taxes && Array.isArray(taxes)) {
            taxes.forEach((tax) => {
                const key = `${tax.code}-${tax.percent}`;
                result[`${key}-total`] = 0;
                result[`${key}-tva`] = 0;
            });
        }

        if (paymentMethods && Array.isArray(paymentMethods)) {
            paymentMethods.forEach((method) => {
                result[`payment-${method.type}`] = 0;
            });
        }

        reports.forEach((report) => {
            if (!report || !report.soap_data || !report.soap_data.daily) {
                return;
            }

            try {
                const receiptsCount =
                    report.soap_data.daily.receipts?.field[0]?.count;
                result.receipts += parseInt(receiptsCount || "0", 10) || 0;
            } catch (e) {
                console.error("Error processing receipts:", e);
            }

            try {
                if (report.soap_data.daily?.payments?.field) {
                    const paymentFields = Array.isArray(
                        report.soap_data.daily.payments.field
                    )
                        ? report.soap_data.daily.payments.field
                        : [report.soap_data.daily.payments.field];

                    paymentFields.forEach((payment) => {
                        if (payment && payment.type != null) {
                            const paymentKey = `payment-${String(payment.type).trim()}`;
                            if (result[paymentKey] === undefined) {
                                result[paymentKey] = 0;
                            }
                            result[paymentKey] +=
                                parseFloat(payment.total || "0") || 0;
                        }
                    });
                }
            } catch (e) {
                console.error("Error processing payments:", e);
            }

            try {
                const taxFields = report.soap_data.daily.taxes?.field;
                if (taxFields) {
                    if (Array.isArray(taxFields)) {
                        taxFields.forEach((tax) => {
                            if (tax && tax.code && tax.percent) {
                                const key = `${tax.code}-${tax.percent}`;
                                if (result[`${key}-total`] !== undefined) {
                                    result[`${key}-total`] +=
                                        parseFloat(tax.cost || "0") || 0;
                                    result[`${key}-tva`] +=
                                        parseFloat(tax.rate || "0") || 0;
                                }
                            }
                        });
                    } else if (taxFields.code && taxFields.percent) {
                        const tax = taxFields;
                        const key = `${tax.code}-${tax.percent}`;
                        if (result[`${key}-total`] !== undefined) {
                            result[`${key}-total`] +=
                                parseFloat(tax.cost || "0") || 0;
                            result[`${key}-tva`] +=
                                parseFloat(tax.rate || "0") || 0;
                        }
                    }
                }
            } catch (e) {
                console.error("Error processing tax fields:", e);
            }
        });

        Object.keys(result).forEach((key) => {
            if (key !== "receipts") {
                result[key] = result[key].toFixed(2);
            }
        });

        return result;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString("ro-MD", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
            });
        } catch (e) {
            console.error("Error formatting date:", e);
            return dateString || "";
        }
    };

    const formatTime = (timeString) => {
        if (!timeString) return "";
        try {
            const time = new Date(`2000-01-01T${timeString}`);
            return time.toLocaleTimeString("ro-MD", {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
            });
        } catch (e) {
            console.error("Error formatting time:", e);
            return timeString || "";
        }
    };

    const formatDateTime = (dateString) => {
        try {
            if (!dateString) return "";
            const parts = dateString.split("T");
            if (parts.length !== 2) return dateString;

            return `${formatDate(parts[0])} ${formatTime(parts[1])}`;
        } catch (e) {
            console.error("Error formatting datetime:", e);
            return dateString || "";
        }
    };

    const getTaxDisplayName = (tax) => {
        if (!tax || !tax.code) return "Fără TVA";

        switch (tax.code) {
            case "A":
                return `Cota A - ${tax.percent}%`;
            case "B":
                return `Cota B - ${tax.percent}%`;
            case "C":
                return `Cota C - ${tax.percent}%`;
            case "_":
                return "Cota Fără TVA";
            default:
                return `Cota ${tax.code} - ${tax.percent}%`;
        }
    };

    const getPaymentMethodName = (methodType) => {
        const paymentMethodName = CONSTANT_PAYMENT_METHODS_DICT[methodType]
            ? CONSTANT_PAYMENT_METHODS_DICT[methodType].methodName
            : methodType;

        return t(paymentMethodName) || paymentMethodName;
    };

    const getReports = () => {
        if (!company || !registerId) return;

        try {
            const selectedCash = company.cash_registers.find(
                (cash) => cash._id === Number.parseInt(registerId)
            );

            if (!selectedCash) {
                console.error("Cash register not found");
                setLoading(false);
                return;
            }

            const params = {
                report_type: "1",
                limit: 10000,
                start: 0,
                date_from: dateFrom,
                date_to: dateTo,
                idnx: company.idno,
                address: selectedCash.address,
                factory: selectedCash.factory,
                key: selectedCash.key,
                model: selectedCash.model,
                name: company.name,
                number: selectedCash.number,
                point: selectedCash.point,
                type: 3,
                ...(fromReportId && { id_from: fromReportId }),
                ...(toReportId && { id_to: toReportId }),
            };

            getAllMyReports(params)
                .then((data) => {
                    if (!data || !data.data || !data.data.data) {
                        throw new Error("Invalid API response format");
                    }

                    const reportsData = data.data.data || [];
                    setReports(reportsData);

                    const taxes = new Map();
                    const usedPaymentTypes = new Set();

                    if (Array.isArray(reportsData)) {
                        reportsData.forEach((report) => {
                            if (report?.soap_data?.daily?.taxes?.field) {
                                const taxFields =
                                    report.soap_data.daily.taxes.field;
                                if (Array.isArray(taxFields)) {
                                    taxFields.forEach((tax) => {
                                        if (tax && tax.code && tax.percent) {
                                            const key = `${tax.code}-${tax.percent}`;
                                            taxes.set(key, {
                                                code: tax.code,
                                                percent: tax.percent,
                                            });
                                        }
                                    });
                                } else if (
                                    taxFields &&
                                    taxFields.code &&
                                    taxFields.percent
                                ) {
                                    const tax = taxFields;
                                    const key = `${tax.code}-${tax.percent}`;
                                    taxes.set(key, {
                                        code: tax.code,
                                        percent: tax.percent,
                                    });
                                }
                            }

                            if (report?.soap_data?.daily?.payments?.field) {
                                let paymentFields =
                                    report.soap_data.daily.payments.field;

                                if (!Array.isArray(paymentFields)) {
                                    paymentFields = [paymentFields];
                                }

                                paymentFields.forEach((payment) => {
                                    if (payment && payment.type != null) {
                                        usedPaymentTypes.add(String(payment.type).trim());
                                    }
                                });
                            }
                        });
                    }

                    const uniqueTaxList = Array.from(taxes.values());
                    setUniqueTaxes(uniqueTaxList);

                    const methods = [];
                    usedPaymentTypes.forEach((paymentType) => {
                        const knownMethod = CONSTANT_PAYMENT_METHODS.find(m => m.type === paymentType);
                        if (knownMethod) {
                            methods.push(knownMethod);
                        } else {
                            methods.push({
                                type: paymentType,
                                methodName: `payment_type_${paymentType}`,
                                printName: `Tip ${paymentType}`,
                                limitToTotalPrice: true,
                            });
                        }
                    });

                    methods.sort((a, b) => a.type.localeCompare(b.type));

                    setUsedPaymentMethods(methods);

                    const reportTotals = calculateTotals(
                        reportsData,
                        uniqueTaxList,
                        methods
                    );
                    setTotals(reportTotals);
                })
                .catch((error) => {
                    console.error("Error fetching reports:", error);
                    setReports([]);
                    setUniqueTaxes([]);
                    setUsedPaymentMethods([]);
                    setTotals(calculateTotals([], [], []));
                })
                .finally(() => {
                    setLoading(false);
                });
        } catch (error) {
            console.error("Error in getReports:", error);
            setLoading(false);
            setReports([]);
            setTotals(calculateTotals([], [], []));
        }
    };

    useEffect(() => {
        if (!company) return;
        getReports();
    }, [company]);

    if ((isCompanyLoading && !company) || (loading && !reports)) {
        return (
            <Box
                sx={{
                    flexGrow: 1,
                    marginTop: 3,
                    marginRight: 3,
                    justifyContent: "center",
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (reports && reports.length === 0) {
        return (
            <Box
                sx={{
                    flexGrow: 1,
                    marginTop: 3,
                    marginRight: 3,
                    justifyContent: "center",
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        padding: 4,
                        borderRadius: 2,
                        textAlign: "center",
                        backgroundColor: "#f9fafb",
                        maxWidth: 400,
                    }}
                >
                    <ReceiptLongIcon
                        sx={{ fontSize: 60, color: "#9e9e9e", mb: 2 }}
                    />
                    <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                    >
                        Nu există rapoarte pentru perioada selectată
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Încercați să modificați intervalul de date sau selectați
                        alt registru de casă.
                    </Typography>
                </Paper>
            </Box>
        );
    }

    const selectedCash = company?.cash_registers.find(
        (cash) => cash._id === Number.parseInt(registerId)
    );
    const firstReport =
        reports && reports.length > 0 ? reports[reports.length - 1] : null;
    const lastReport = reports && reports.length > 0 ? reports[0] : null;
    const createdDate = selectedCash?.changes?.created;
    const updatedDate = selectedCash?.changes?.mev_auth_changes[0]?.updated;

    const getTaxUpdatedDate = (tax) => {
        const taxChanges = selectedCash?.changes?.tax_changes;
        if (!tax || !tax.code || !tax.percent) return "";

        const taxChange = taxChanges?.find(
            (item) =>
                item.tax.code === tax.code &&
                item.tax.percent === parseFloat(tax.percent)
        );

        return `${formatDateTime(taxChange?.updated)}`;
    };

    return (
        <Box
            sx={{
                flexGrow: 1,
                marginTop: 3,
                marginRight: 3,
            }}
        >
            <div style={{ display: "none" }}>
                <PrintTable
                    ref={printRef}
                    company={company}
                    registerId={registerId}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    reports={reports}
                    uniqueTaxes={uniqueTaxes}
                    usedPaymentMethods={usedPaymentMethods}
                    totals={totals}
                    firstReport={firstReport}
                    lastReport={lastReport}
                />
            </div>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    mb: 3,
                }}
            >
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PrintIcon />}
                    onClick={handlePrint}
                    sx={{
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        borderRadius: "8px",
                        textTransform: "none",
                        px: 3,
                    }}
                >
                    {t("print")}
                </Button>
            </Box>

            <Paper
                elevation={3}
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    borderRadius: 2,
                    padding: 0,
                    boxSizing: "border-box",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                }}
            >
                <Box
                    sx={{
                        background: "#f5f7fa",
                        borderBottom: "1px solid #e0e5eb",
                        py: 2,
                        px: 3,
                        borderTopLeftRadius: "8px",
                        borderTopRightRadius: "8px",
                        mb: 0,
                    }}
                >
                    <Typography
                        variant="h6"
                        component="h1"
                        fontWeight="medium"
                        color="#2c3e50"
                        sx={{ mb: 0.5 }}
                    >
                        Dare de seamă periodică
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <CalendarTodayIcon
                            sx={{ fontSize: 16, color: "#64748b" }}
                        />
                        <Typography variant="body2" color="#64748b">
                            Perioada:{" "}
                            <span style={{ fontWeight: 500 }}>
                                {formatDate(dateFrom)} - {formatDate(dateTo)}
                            </span>
                        </Typography>
                    </Stack>
                </Box>

                <Box sx={{ p: 4 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            mb: 4,
                            borderRadius: 2,
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                            sx={{ mb: 2 }}
                        >
                            <StorefrontIcon sx={{ color: "#1e40af" }} />
                            <Typography
                                variant="h6"
                                sx={{ color: "#1e40af" }}
                                fontWeight="medium"
                            >
                                Informații Companie
                            </Typography>
                        </Stack>

                        <Stack spacing={1} sx={{ ml: 5 }}>
                            <Stack direction="row" spacing={1}>
                                <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                    color="text.secondary"
                                    sx={{ width: 130 }}
                                >
                                    Organizația:
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="text.primary"
                                    fontWeight="500"
                                >
                                    {company?.name || "-"}
                                </Typography>
                            </Stack>

                            <Stack direction="row" spacing={1}>
                                <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                    color="text.secondary"
                                    sx={{ width: 130 }}
                                >
                                    Cod fiscal:
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="text.primary"
                                    fontWeight="500"
                                >
                                    {company?.idno || "-"}
                                </Typography>
                            </Stack>
                        </Stack>
                    </Paper>

                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            mb: 4,
                            borderRadius: 2,
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                            sx={{ mb: 2 }}
                        >
                            <AccountBalanceWalletIcon
                                sx={{ color: "#1e40af" }}
                            />
                            <Typography
                                variant="h6"
                                fontWeight="medium"
                                sx={{ color: "#1e40af" }}
                            >
                                Datele despre EKassa
                            </Typography>
                        </Stack>

                        <Grid container spacing={2} sx={{ ml: 3 }}>
                            <GridItem
                                label="Nr. de serie:"
                                value={
                                    company?.cash_registers.find(
                                        (cash) =>
                                            cash._id ===
                                            Number.parseInt(registerId)
                                    )?.factory || "-"
                                }
                            />

                            <GridItem
                                label="creat la data"
                                value={
                                    createdDate
                                        ? formatDateTime(createdDate)
                                        : "-"
                                }
                            />

                            <GridItem
                                label="Nr. de înregistrare"
                                value={
                                    company?.cash_registers.find(
                                        (cash) =>
                                            cash._id ===
                                            Number.parseInt(registerId)
                                    )?.number || "-"
                                }
                            />

                            <GridItem
                                label="fiscalizat la data"
                                value={
                                    updatedDate
                                        ? formatDateTime(updatedDate)
                                        : "-"
                                }
                            />
                        </Grid>
                    </Paper>

                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            mb: 4,
                            borderRadius: 2,
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                            sx={{ mb: 2 }}
                        >
                            <ReceiptLongIcon sx={{ color: "#1e40af" }} />
                            <Typography
                                variant="h6"
                                fontWeight="medium"
                                sx={{ color: "#1e40af" }}
                            >
                                Detalii Rapoarte
                            </Typography>
                        </Stack>

                        <Grid container spacing={2} sx={{ ml: 3 }}>
                            <GridItem
                                label="Primul raport Z"
                                value={`${
                                    firstReport?.soap_data?.report || "-"
                                } din ${
                                    firstReport?.soap_data?.datetime
                                        ? formatDateTime(
                                              firstReport.soap_data.datetime
                                          )
                                        : "-"
                                }`}
                            />

                            <GridItem
                                label="Ultimul raport Z"
                                value={`${
                                    lastReport?.soap_data?.report || "-"
                                } din ${
                                    lastReport?.soap_data?.datetime
                                        ? formatDateTime(
                                              lastReport.soap_data.datetime
                                          )
                                        : "-"
                                }`}
                            />

                            <GridItem
                                special
                                label="Cantitatea bonurilor"
                                value={totals?.receipts || 0}
                            />
                        </Grid>
                    </Paper>

                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            mb: 4,
                            borderRadius: 2,
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                            sx={{ mb: 2 }}
                        >
                            <PercentIcon sx={{ color: "#1e40af" }} />
                            <Typography
                                variant="h6"
                                fontWeight="medium"
                                sx={{ color: "#1e40af" }}
                            >
                                Cote TVA
                            </Typography>
                        </Stack>

                        <TableContainer sx={{ px: 3 }}>
                            <Table size="small" sx={{ tableLayout: "fixed" }}>
                                <TableBody>
                                    {uniqueTaxes.map((tax, index) => (
                                        <TableRow
                                            key={`tax-row-${index}`}
                                            hover
                                        >
                                            <TableCell
                                                sx={{
                                                    border: "none",
                                                    pl: 2,
                                                    fontWeight: "medium",
                                                    whiteSpace: "nowrap",
                                                    width: "max-content",
                                                }}
                                            >
                                                {getTaxDisplayName(tax)}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    border: "none",
                                                    color: "text.secondary",
                                                    whiteSpace: "nowrap",
                                                    px: 0,
                                                    pl: 3,
                                                }}
                                            >
                                                {getTaxUpdatedDate(tax)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            mb: 4,
                            borderRadius: 2,
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                            sx={{ mb: 3 }}
                        >
                            <PaymentIcon sx={{ color: "#1e40af" }} />
                            <Typography
                                variant="h6"
                                fontWeight="medium"
                                sx={{ color: "#1e40af" }}
                            >
                                Vînzări
                            </Typography>
                        </Stack>

                        <TableContainer sx={{ mb: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                fontWeight: "bold",
                                                color: "text.secondary",
                                                borderBottom:
                                                    "2px solid #1976d2",
                                                width: "60%",
                                            }}
                                        >
                                            Categorie
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                fontWeight: "bold",
                                                color: "text.secondary",
                                                borderBottom:
                                                    "2px solid #1976d2",
                                                width: "20%",
                                            }}
                                        >
                                            BRUT
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                fontWeight: "bold",
                                                color: "text.secondary",
                                                borderBottom:
                                                    "2px solid #1976d2",
                                                width: "20%",
                                            }}
                                        >
                                            Valoare TVA
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {uniqueTaxes.map((tax, index) => {
                                        const key = `${tax.code}-${tax.percent}`;
                                        const hasValue =
                                            parseFloat(
                                                totals?.[`${key}-total`] || 0
                                            ) > 0;

                                        return (
                                            <TableRow
                                                key={`sale-row-${index}`}
                                                hover
                                                sx={{
                                                    backgroundColor:
                                                        index % 2 === 0
                                                            ? "rgba(0, 0, 0, 0.02)"
                                                            : "transparent",
                                                }}
                                            >
                                                <TableCell
                                                    sx={{
                                                        fontWeight: hasValue
                                                            ? 500
                                                            : 400,
                                                        color: hasValue
                                                            ? "text.primary"
                                                            : "text.secondary",
                                                    }}
                                                >
                                                    {getTaxDisplayName(tax)}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        fontWeight: hasValue
                                                            ? 500
                                                            : 400,
                                                        color: hasValue
                                                            ? "text.primary"
                                                            : "text.secondary",
                                                    }}
                                                >
                                                    {formatCurrency(
                                                        totals?.[
                                                            `${key}-total`
                                                        ] || 0
                                                    )}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        fontWeight: hasValue
                                                            ? 500
                                                            : 400,
                                                        color: hasValue
                                                            ? "text.primary"
                                                            : "text.secondary",
                                                    }}
                                                >
                                                    {formatCurrency(
                                                        totals?.[
                                                            `${key}-tva`
                                                        ] || 0
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    <TableRow
                                        sx={{
                                            backgroundColor: "#f1f5f9",
                                            "& td": {
                                                fontWeight: "bold",
                                                py: 1.5,
                                            },
                                        }}
                                    >
                                        <TableCell sx={{ color: "#1e40af" }}>
                                            TOTAL
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{ color: "#1e40af" }}
                                        >
                                            {formatCurrency(
                                                uniqueTaxes.reduce(
                                                    (sum, tax) => {
                                                        const key = `${tax.code}-${tax.percent}`;
                                                        return (
                                                            sum +
                                                            parseFloat(
                                                                totals?.[
                                                                    `${key}-total`
                                                                ] || 0
                                                            )
                                                        );
                                                    },
                                                    0
                                                )
                                            )}
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{ color: "#1e40af" }}
                                        >
                                            {formatCurrency(
                                                uniqueTaxes.reduce(
                                                    (sum, tax) => {
                                                        const key = `${tax.code}-${tax.percent}`;
                                                        return (
                                                            sum +
                                                            parseFloat(
                                                                totals?.[
                                                                    `${key}-tva`
                                                                ] || 0
                                                            )
                                                        );
                                                    },
                                                    0
                                                )
                                            )}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            mb: 2,
                            borderRadius: 2,
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                            sx={{ mb: 3 }}
                        >
                            <PaymentIcon sx={{ color: "#1e40af" }} />
                            <Typography
                                variant="h6"
                                fontWeight="medium"
                                sx={{ color: "#1e40af" }}
                            >
                                Metode de plată
                            </Typography>
                        </Stack>

                        <Stack spacing={2} sx={{ px: 2 }}>
                            {usedPaymentMethods
                                .filter((method) => {
                                    const key = `payment-${method.type}`;
                                    return parseFloat(totals?.[key] || 0) !== 0;
                                })
                                .map((method, index) => {
                                    const key = `payment-${method.type}`;
                                    const value = parseFloat(totals?.[key] || 0);
                                    const color = "#1e40af";

                                    return (
                                        <Box
                                            key={`payment-row-${index}`}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                p: 1.5,
                                                borderRadius: 1,
                                                backgroundColor: `${color}10`,
                                                border: `1px solid ${color}40`,
                                            }}
                                        >
                                            <Stack
                                                direction="row"
                                                spacing={2}
                                                alignItems="center"
                                            >
                                                {method.type === "1" ? (
                                                    <AccountBalanceWalletIcon
                                                        sx={{ color }}
                                                    />
                                                ) : method.type === "2" ? (
                                                    <PaymentIcon sx={{ color }} />
                                                ) : (
                                                    <PaymentIcon sx={{ color }} />
                                                )}

                                                <Typography
                                                    variant="body1"
                                                    fontWeight="medium"
                                                    color="text.primary"
                                                >
                                                    {getPaymentMethodName(
                                                        method.type
                                                    )}
                                                </Typography>
                                            </Stack>

                                            <Typography
                                                variant="body1"
                                                fontWeight="bold"
                                                sx={{ color }}
                                            >
                                                {formatCurrency(value)}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                        </Stack>
                    </Paper>

                    <Box
                        sx={{
                            mt: 4,
                            pt: 2,
                            borderTop: "1px solid #eaeaea",
                            textAlign: "center",
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            Document generat la{" "}
                            {formatDateTime(new Date().toISOString())}
                        </Typography>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

const GridItem = ({ label, value, special = false }) => (
    <Box sx={{ mb: 1.5, display: "flex", width: "100%" }}>
        <Typography
            variant="body2"
            fontWeight="medium"
            color="text.secondary"
            sx={{ width: 150 }}
        >
            {label}
        </Typography>
        <Typography
            variant="body1"
            sx={{
                fontWeight: 500,
                color: "text.primary",
                fontSize: "inherit",
            }}
        >
            {value}
        </Typography>
    </Box>
);

const Grid = ({ children, container, spacing = 2, ...props }) => (
    <Box
        sx={{
            display: "flex",
            flexWrap: "wrap",
            width: "100%",
            ...props.sx,
        }}
        {...props}
    >
        {children}
    </Box>
);

export default PeriodicReports;
